/**
 * Habit arithmetic (spec §9.7 `habits`, §9.19 streak-free): which days a view shows, whether a day
 * counts, and the neutral summary of a row. No streaks, nothing about missed days.
 */
import type { Counter } from "../views/counters/ops";

export const dayKey = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Monday of the ISO week containing `d` (local time). */
export function weekStart(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
}

/** The days a habits view shows: a Monday-to-Sunday week, or a calendar month; `offset` pages back/forth. */
export function viewDays(now: Date, range: "week" | "month", offset = 0): Date[] {
  if (range === "week") {
    const start = weekStart(now);
    start.setDate(start.getDate() + offset * 7);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }
  const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const days = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  return Array.from({ length: days }, (_, i) => new Date(first.getFullYear(), first.getMonth(), i + 1));
}

/** Does this day count for the habit? check: ticked; count: reached the daily goal, or anything without one. */
export function dayCounts(h: Pick<Counter, "kind" | "goal" | "period">, value: number): boolean {
  if (value <= 0) return false;
  if (h.kind === "check") return true;
  if (h.goal && (h.period ?? "day") === "day") return value >= h.goal;
  return true;
}

export interface HabitSummary {
  /** days in view (up to today) that count */
  days: number;
  /** for a weekly goal: the week's total so far and the goal */
  week?: { done: number; goal: number };
  /** the days that counted, for the "why?" */
  counted: string[];
}

/** The neutral summary of a row over the days in view up to and including today. */
export function summarize(h: Counter, days: Date[], today: Date): HabitSummary {
  const end = dayKey(today);
  const shown = days.map(dayKey).filter((k) => k <= end);
  const counted = shown.filter((k) => dayCounts(h, h.entries[k] ?? 0));
  const out: HabitSummary = { days: counted.length, counted };
  if (h.goal && h.period === "week") {
    const ws = weekStart(today);
    let done = 0;
    for (let i = 0; i < 7; i++) {
      const d = new Date(ws);
      d.setDate(ws.getDate() + i);
      const k = dayKey(d);
      if (k > end) break;
      const v = h.entries[k] ?? 0;
      done += h.kind === "check" ? (v > 0 ? 1 : 0) : v;
    }
    out.week = { done, goal: h.goal };
  }
  return out;
}

/** Done days of a soft recurring task, from its history (skips do not count). */
export function recurringDoneDays(props: Record<string, unknown>): Set<string> {
  const h = props["rec.history"];
  const out = new Set<string>();
  if (Array.isArray(h))
    for (const e of h as { at?: string; skipped?: boolean }[])
      if (e && typeof e.at === "string" && !e.skipped) out.add(dayKey(new Date(e.at)));
  return out;
}
